import * as React from 'react';
import { useQuery } from 'react-query';
import {
  Activity,
  CheckCircle2,
  FileMinus2,
  FilePlus2,
  Loader,
  UserCircle,
  XCircle,
} from 'lucide-react';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { useFetchViagensControle } from 'hooks/api/viagens-controle';
import { Formatter } from 'utils';
import type { ViagemControle, ViagemControleActivity } from './types';

type Props = {
  viagem: ViagemControle | null;
  onOpenChange: (open: boolean) => void;
};

const formatDate = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm:ss' }) : '---';
};

type ActivityDataInfo = {
  viagemControleId?: number;
  idViagem?: string | null;
  placa?: string | null;
  viagemBillingId?: number | null;
  invoiceNumber?: number | null;
  justificationId?: number | null;
  rejectionId?: number | null;
  justificationDescription?: string | null;
  comment?: string | null;
};

const parseDataInfo = (dataInfo?: string | null): ActivityDataInfo => {
  if (!dataInfo) return {};

  try {
    return JSON.parse(dataInfo);
  } catch {
    return {};
  }
};

const getActivityPresentation = (activity: ViagemControleActivity) => {
  switch (activity.operation) {
    case 'APPROVE':
      return {
        title: 'Viagem aprovada',
        tone: 'text-emerald-700 bg-emerald-50 border-emerald-200',
        icon: <CheckCircle2 className="h-5 w-5" />,
      };
    case 'REJECT':
      return {
        title: 'Viagem reprovada',
        tone: 'text-red-700 bg-red-50 border-red-200',
        icon: <XCircle className="h-5 w-5" />,
      };
    case 'ADD_TO_BILLING':
      return {
        title: 'Viagem adicionada à fatura',
        tone: 'text-blue-700 bg-blue-50 border-blue-200',
        icon: <FilePlus2 className="h-5 w-5" />,
      };
    case 'REMOVE_FROM_BILLING':
      return {
        title: 'Viagem removida da fatura',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
        icon: <FileMinus2 className="h-5 w-5" />,
      };
    case 'REVERSE':
      return {
        title: 'Viagem estornada',
        tone: 'text-amber-700 bg-amber-50 border-amber-200',
        icon: <FileMinus2 className="h-5 w-5" />,
      };
    default:
      return {
        title: activity.description,
        tone: 'text-slate-700 bg-slate-50 border-slate-200',
        icon: <Activity className="h-5 w-5" />,
      };
  }
};

const getActivitySummary = (
  activity: ViagemControleActivity,
  dataInfo: ActivityDataInfo,
  fallbackViagem?: ViagemControle | null
) => {
  const idViagem = dataInfo.idViagem || fallbackViagem?.idViagem || fallbackViagem?.id;
  const viagemLabel = `Viagem de ID ${idViagem || '---'}`;

  switch (activity.operation) {
    case 'APPROVE':
      return `${viagemLabel} foi aprovada.`;
    case 'REJECT':
      return `${viagemLabel} foi reprovada.`;
    case 'ADD_TO_BILLING':
      return `${viagemLabel} foi adicionada à fatura #${
        dataInfo.viagemBillingId || dataInfo.invoiceNumber || '---'
      }.`;
    case 'REMOVE_FROM_BILLING':
      return `${viagemLabel} foi removida da fatura #${
        dataInfo.viagemBillingId || dataInfo.invoiceNumber || '---'
      }.`;
    default:
      return activity.description;
  }
};

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="rounded border border-slate-200 bg-white px-3 py-2">
    <span className="block font-GilroyBold text-[11px] uppercase text-slate-500">
      {label}
    </span>
    <span className="mt-1 block break-words whitespace-pre-wrap font-sans text-sm text-slate-800">
      {value || '---'}
    </span>
  </div>
);

const clearBodyPointerLock = () => {
  [0, 50, 150, 300].forEach((delay) => {
    window.setTimeout(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, delay);
  });
};

export const ViagemControleActivitiesDialog = ({
  viagem,
  onOpenChange,
}: Props) => {
  const { fetchViagemControleById } = useFetchViagensControle();
  const open = Boolean(viagem);

  const viagemQuery = useQuery({
    queryKey: ['viagem-controle-activities', viagem?.id],
    queryFn: () => fetchViagemControleById(viagem?.id as number),
    enabled: Boolean(viagem?.id),
    refetchOnWindowFocus: false,
  });

  const activities = React.useMemo(() => {
    const activityEntries = (viagemQuery.data?.activities ?? [])
      .filter((activity) => activity.operation !== 'REJECT')
      .map((activity) => ({ activity, rejection: undefined }));
    const rejectionEntries = (viagemQuery.data?.rejections ?? []).map(
      (rejection) => ({
        activity: {
          id: rejection.id,
          description: `Viagem ${
            viagemQuery.data?.idViagem || viagemQuery.data?.id || '---'
          } reprovada`,
          operation: 'REJECT' as const,
          table: 'viagens_controle' as const,
          dataInfo: JSON.stringify({
            viagemControleId: rejection.viagemControleId,
            justificationId: rejection.justificationId,
            rejectionId: rejection.id,
            viagemBillingId: viagemQuery.data?.billingItem?.viagemBillingId,
          }),
          createdAt: rejection.createdAt,
          user: rejection.user,
        },
        rejection,
      })
    );

    return [...activityEntries, ...rejectionEntries].sort((a, b) => {
      return (
        new Date(b.activity.createdAt).getTime() -
        new Date(a.activity.createdAt).getTime()
      );
    });
  }, [viagemQuery.data]);

  const currentViagem = viagemQuery.data ?? viagem;

  const closeDialog = React.useCallback(() => {
    onOpenChange(false);
    clearBodyPointerLock();
  }, [onOpenChange]);

  React.useEffect(() => {
    if (!open) {
      clearBodyPointerLock();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && closeDialog()}>
      <DialogContent className="max-w-[800px] bg-white p-10">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <Activity className="h-6 w-6 text-slate-900" />
          <h2 className="font-GilroyBold text-xl text-slate-900">
            Registro de atividades da viagem - {viagem?.idViagem || viagem?.id}
          </h2>
        </header>

        {viagemQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-slate-500" />
          </div>
        ) : (
          <div className="relative mt-6 max-h-[60vh] overflow-y-auto pl-8 pr-2">
            <div className="absolute bottom-0 left-3 top-0 w-px bg-slate-200" />

            {activities.map(({ activity, rejection }) => {
              const presentation = getActivityPresentation(activity);
              const dataInfo = parseDataInfo(activity.dataInfo);
              const idViagem = dataInfo.idViagem || currentViagem?.idViagem;
              const placa = dataInfo.placa || currentViagem?.placa;
              const invoiceNumber =
                dataInfo.viagemBillingId ||
                currentViagem?.billingItem?.viagemBillingId;
              const rejectionJustification = rejection?.justification;
              const justificationDescription =
                rejectionJustification?.description ||
                dataInfo.justificationDescription;
              const rejectionComment = rejection?.comment || dataInfo.comment;
              const summary = getActivitySummary(activity, dataInfo, currentViagem);

              return (
                <article key={activity.id} className="relative mb-4">
                  <span className="absolute -left-[31px] top-8 h-5 w-5 rounded-full border-4 border-slate-300 bg-white" />
                  <div className="rounded-md border border-slate-100 bg-slate-50 p-4 shadow-md">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-9 w-9 items-center justify-center rounded-full border ${presentation.tone}`}
                        >
                          {presentation.icon}
                        </span>
                        <div>
                          <strong className="font-GilroyBold text-base text-slate-900">
                            {presentation.title}
                          </strong>
                          <div className="mt-1 flex items-center gap-2 font-sans text-xs text-slate-500">
                            <UserCircle className="h-4 w-4" />
                            {activity.user?.name || 'Usuário não identificado'}
                          </div>
                        </div>
                      </div>
                      <span className="font-GilroyBold text-xs text-slate-600">
                        {formatDate(activity.createdAt)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <DetailItem label="ID viagem" value={idViagem} />
                      <DetailItem label="Placa" value={placa} />
                      <DetailItem
                        label="Fatura"
                        value={invoiceNumber ? `#${invoiceNumber}` : undefined}
                      />
                    </div>

                    {(activity.operation === 'REJECT' || activity.operation === 'REVERSE') && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <DetailItem
                          label="Justificativa"
                          value={justificationDescription}
                        />
                        <DetailItem
                          label="Comentário"
                          value={rejectionComment || dataInfo.comment}
                        />
                      </div>
                    )}

                    <p className="mt-4 rounded border border-slate-200 bg-white px-3 py-2 font-sans text-sm text-slate-700">
                      {summary}
                    </p>
                  </div>
                </article>
              );
            })}

            {!activities.length && (
              <div className="rounded-md bg-slate-50 p-6 text-center font-sans text-sm text-slate-600">
                Nenhuma atividade registrada para esta viagem.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
